import { ChartContainer } from '../../shared/ChartContainer';
import { EmptyState } from '../../shared/EmptyState';
import { BRAND_COLOR } from '../../shared/analytics-constants';
import type { FpActionItem } from '../../../../types/analytics.types';

type AdvisorActionsBarProps = {
  actions: FpActionItem[];
  /** Whether a single partner company is selected in the filter. */
  firmSelected?: boolean;
};

const TITLE = 'Most Common Advisor Actions';

export const AdvisorActionsBar = ({ actions, firmSelected = false }: AdvisorActionsBarProps) => {
  // Most actions scope to the selected firm; Navigator Searches stays all-firms
  // (marked with a badge), so the subtitle must not claim "across all partners".
  const subtitle = firmSelected
    ? 'Ranked action volume for the selected firm in the period'
    : 'Ranked action volume across all partners in the selected period';

  if (actions.length === 0 || actions.every((a) => a.count === 0)) {
    return (
      <ChartContainer title={TITLE} subtitle={subtitle}>
        <EmptyState message="No advisor actions for the selected filters." />
      </ChartContainer>
    );
  }

  const max = Math.max(...actions.map((a) => a.count), 1);

  return (
    <ChartContainer title={TITLE} subtitle={subtitle}>
      <div className="space-y-4">
        {actions.map((action) => (
          <div key={action.key} className="flex items-center gap-4">
            <div className="w-44 flex items-center gap-2 text-sm text-gray-700">
              <span>{action.label}</span>
              {action.isNew && (
                <span className="text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                  New
                </span>
              )}
              {firmSelected && !action.firmScoped && (
                <span
                  className="text-[10px] font-semibold uppercase tracking-wide bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded"
                  title="This metric is not scoped to the selected firm"
                >
                  All firms
                </span>
              )}
            </div>
            <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{ width: `${(action.count / max) * 100}%`, backgroundColor: BRAND_COLOR }}
              />
            </div>
            <div className="w-16 text-right text-sm font-medium text-gray-900">
              {action.count.toLocaleString('en-US')}
            </div>
          </div>
        ))}
      </div>
    </ChartContainer>
  );
};
